import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationStepsEnum,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    shouldCollectPostalCode,
    VerificationService,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    FormSelectChoice,
    TeacherPersonalInfoViewModel,
    Organization,
    TeacherPersonalInfoResponse,
    Locale,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    getAvailableLocaleChoices,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    FormFooterComponent as FormFooter,
    LogoComponent,
    PostalCodeComponent,
    ChangeLocaleComponent,
    setMetadata,
    ErrorId,
} from '@sheerid/jslib';

import { EmailComponent as Email } from '../FormFields/EmailComponent';
import { ConfirmEmailComponent as ConfirmEmail } from '../FormFields/ConfirmEmailComponent';
import { CourseTypeComponent } from '../FormFields/CourseTypeComponent';
import { getCourseTypeChoices, mapSoftwareTypeToHumanReadable, getClickTrackingMessage } from '../../lib/stepComponentHelpers';
import { getQueryParamsByName, sendPostMessage } from '../../lib/utils/browser';
import { SoftwareType } from '../../lib/types/types';
import { StepErrorComponent } from './StepErrorComponent';
import { TeacherSchoolComponent } from '../FormFields/TeacherSchoolComponent';


interface StepTeacherPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepTeacherPersonalInfo = ({ intl, verificationService }: StepTeacherPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as TeacherPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as TeacherPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };
    const softwareType = getQueryParamsByName('type') as SoftwareType;
    const softwareTypeMessage = mapSoftwareTypeToHumanReadable(softwareType, intl);
    const clickTrackingMessage = getClickTrackingMessage('collectStudentPersonalInfo', softwareType);
    const [firstRender, setFirstRender] = React.useState(true);
    const countryParam = getQueryParamsByName('country');

    const updateTeacherViewModel = (key: keyof TeacherPersonalInfoViewModel, value: any) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    React.useEffect(() => {
        if (firstRender) {
            updateTeacherViewModel('birthDate', '');
            updateTeacherViewModel('fieldsToSkipValidation', ['birthDate']);
            setMetadata({ type: softwareType || '' });
            setFirstRender(false);
        }
    }, [firstRender, softwareType]);

    if (!softwareTypeMessage || !countryParam || (countryParam !== 'US' && countryParam !== 'CA')) {
        return <StepErrorComponent verificationService={verificationService} errorId="invalidSoftwareType" />;
    }

    return (
        <div id="sid-step-teacher-personal-info" className="sid-l-container">


            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Email" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and email address must match what is on record with your school." />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.title"
                            defaultMessage="Educator Registration"
                        />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        {softwareType === 'tax' ?
                            <>
                                {
                                    countryParam === 'CA' ?
                                        <FormattedHTMLMessage
                                            id="step.personalInfo.subtitle"
                                            defaultMessage="Educators and staff at accredited academic institutions in the United States are eligible. Complete the form below to confirm your eligibility and unlock your free license for {software}."
                                            values={{
                                                software: softwareTypeMessage,
                                            }}
                                        />
                                        :
                                        <FormattedHTMLMessage
                                            id="step.personalInfo.subtitleUS"
                                            defaultMessage="Educators and staff at accredited academic institutions in the United States are eligible. Complete the form below to confirm your eligibility and unlock your free license for ProConnect."
                                        />
                                }
                            </>
                        :
                        <FormattedHTMLMessage
                            id="step.personalInfo.subtitle"
                            defaultMessage="Educators and staff at accredited academic institutions in the United States are eligible. Complete the form below to confirm your eligibility and unlock your free license for {software}."
                            values={{
                                software: softwareTypeMessage,
                            }}
                        />
                        }
                        &nbsp;
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }
            {
                localeChoices.length > 1 && (countryParam === 'CA') ?
                    <ChangeLocaleComponent
                        options={localeChoices}
                        value={viewModel.localeChoice || defaultLocaleChoice}
                        isErrored={false}
                        onChange={(localeChoice: FormSelectChoice<Locale, string>) => {
                            updateLocale(localeChoice)
                            setMetadata({locale: localeChoice.value})
                        }}
                    />
                : null
            }

            <div className="sid-required-fields sid-l-space-top-md">
                <FormattedHTMLMessage
                    id="requiredFields"
                    defaultMessage= "All fields are required"
                />
            </div>

            <div className="sid-l-space-top-md"></div>

            <FirstName
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastName
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Must be your school-issued email address"
                    />
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <ConfirmEmail
                value={viewModel.metadata && viewModel.metadata['confirm-email'] ? viewModel.metadata['confirm-email'] : ''}
                isErrored={!!fieldValidationErrors['confirm-email']}
                errorId={fieldValidationErrors['confirm-email'] as ErrorId}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal or school email is fine"
                    />
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, 'confirm-email': newValue });
                        updateFieldValidationErrorsByFieldId('confirm-email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <TeacherSchoolComponent
                value={(verificationService.viewModel as TeacherPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <CourseTypeComponent
                options={getCourseTypeChoices(intl)}
                value={
                    viewModel.metadata && viewModel.metadata['course-type'] ?
                    {
                        value: viewModel.metadata['course-type'],
                        label: intl.formatMessage({ id: `${(viewModel.metadata['course-type'] as string).toLowerCase()}`, defaultMessage: `${viewModel.metadata['course-type']}` }),
                    }
                    : ''
                }
                isErrored={!!fieldValidationErrors['course-type']}
                onChange={(majorChoice: FormSelectChoice<Locale, string>) => {
                    updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, 'course-type': majorChoice.value });
                    updateFieldValidationErrorsByFieldId('course-type', majorChoice.value, verificationService);
                }}
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue: string) => {
                                updateTeacherViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                : null
            }
            {
                shouldCollectPostalCode(verificationService.programTheme, viewModel) ?
                <PostalCodeComponent
                    isErrored={!!fieldValidationErrors.postalCode}
                    onChange={(newValue: string) => {
                        updateTeacherViewModel('postalCode', newValue);
                        updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                    }}
                    value={viewModel.postalCode}
                />
                :
                null
            }
            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => {
                            sendPostMessage(clickTrackingMessage, 'https://www.intuit.com');
                            submitForm(viewModel, verificationService, VerificationStepsEnum.collectTeacherPersonalInfo);
                        }}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch ?
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            :
                                <FormattedHTMLMessage id="verifyMyTeacherStatus" defaultMessage="Verify My Teacher Status" />
                            }
                        </span>
                    </button>
                </div>
            </div>
            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepTeacherPersonalInfoComponent = injectIntl(StepTeacherPersonalInfo);
